// ============================================================
// LEGAL PAGE - Terms, Privacy, Rules, AMOE, Responsible Gaming
// ============================================================

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  VStack,
  HStack,
  Box,
  Text,
  Heading,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Divider,
  List,
  ListItem,
  Link,
  Icon,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import { FileText, Shield, Scale, Gift, Heart, ArrowLeft, Mail, Globe, ShieldCheck, Wallet, MapPin } from 'lucide-react';
import { GEO_CONFIG } from '../services/geoService';
import { isStoreSafe } from '../utils/storeSafe';
import PokerButton from '../components/ui/PokerButton';
import PokerCard from '../components/ui/PokerCard';
import { POKER_COLORS } from '../theme/designSystem';
import complianceService from '../services/complianceService';
import { PlatformSettings, DEFAULT_PLATFORM_SETTINGS, US_STATES } from '../types/complianceTypes';

const LegalPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<PlatformSettings>({
    ...DEFAULT_PLATFORM_SETTINGS,
    updatedAt: new Date(),
    updatedBy: 'system',
  });
  
  const tabParam = searchParams.get('tab');
  const tabIndex = {
    'terms': 0,
    'privacy': 1,
    'rules': 2,
    'fairness': 3,
    'amoe': 4,
    'responsible': 5,
    'payouts': 6,
    'geo': 7,
  }[tabParam || 'terms'] || 0;

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const platformSettings = await complianceService.getPlatformSettings();
      setSettings(platformSettings);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // The enforced list lives in geoService (and the server); platform settings
  // can add to it but never shrink what the app actually blocks.
  const restrictedStateCodes = Array.from(new Set([...GEO_CONFIG.RESTRICTED_STATES, ...(settings.restrictedStates || [])])).sort();
  const restrictedStateNames = restrictedStateCodes
    .map(code => US_STATES[code] || code)
    .join(', ');

  return (
    <Container maxW="4xl" py={8}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="center">
          <HStack spacing={3}>
            <PokerButton
              variant="secondary"
              size="sm"
              onClick={() => navigate(-1)}
              leftIcon={<ArrowLeft size={16} />}
            >
              Back
            </PokerButton>
            <Heading size="lg" color={POKER_COLORS.cyan}>
              Legal Information
            </Heading>
          </HStack>
        </HStack>

        {/* Tabs */}
        <PokerCard p={0} overflow="hidden">
          <Tabs 
            index={tabIndex} 
            colorScheme="cyan"
            onChange={(index) => {
              const tabs = ['terms', 'privacy', 'rules', 'fairness', 'amoe', 'responsible', 'payouts', 'geo'];
              navigate(`/legal?tab=${tabs[index]}`);
            }}
          >
            <TabList 
              bg={POKER_COLORS.darkBase} 
              overflowX="auto"
              borderBottom={`1px solid ${POKER_COLORS.cyan}33`}
            >
              <Tab 
                color={POKER_COLORS.accentCyan} 
                _selected={{ color: POKER_COLORS.cyan, borderColor: POKER_COLORS.cyan }}
                whiteSpace="nowrap"
              >
                <Icon as={FileText} mr={2} boxSize={4} /> Terms
              </Tab>
              <Tab 
                color={POKER_COLORS.accentCyan} 
                _selected={{ color: POKER_COLORS.cyan, borderColor: POKER_COLORS.cyan }}
                whiteSpace="nowrap"
              >
                <Icon as={Shield} mr={2} boxSize={4} /> Privacy
              </Tab>
              <Tab 
                color={POKER_COLORS.accentCyan} 
                _selected={{ color: POKER_COLORS.cyan, borderColor: POKER_COLORS.cyan }}
                whiteSpace="nowrap"
              >
                <Icon as={Scale} mr={2} boxSize={4} /> Rules
              </Tab>
              <Tab
                color={POKER_COLORS.accentCyan}
                _selected={{ color: POKER_COLORS.cyan, borderColor: POKER_COLORS.cyan }}
                whiteSpace="nowrap"
              >
                <Icon as={ShieldCheck} mr={2} boxSize={4} /> Fair Play
              </Tab>
              <Tab
                color={POKER_COLORS.accentCyan}
                _selected={{ color: POKER_COLORS.cyan, borderColor: POKER_COLORS.cyan }}
                whiteSpace="nowrap"
              >
                <Icon as={Gift} mr={2} boxSize={4} /> Free Entry
              </Tab>
              <Tab 
                color={POKER_COLORS.accentCyan} 
                _selected={{ color: POKER_COLORS.cyan, borderColor: POKER_COLORS.cyan }}
                whiteSpace="nowrap"
              >
                <Icon as={Heart} mr={2} boxSize={4} /> Responsible Gaming
              </Tab>
              <Tab
                color={POKER_COLORS.accentCyan}
                _selected={{ color: POKER_COLORS.cyan, borderColor: POKER_COLORS.cyan }}
                whiteSpace="nowrap"
              >
                <Icon as={Wallet} mr={2} boxSize={4} /> Payouts &amp; Refunds
              </Tab>
              <Tab
                color={POKER_COLORS.accentCyan}
                _selected={{ color: POKER_COLORS.cyan, borderColor: POKER_COLORS.cyan }}
                whiteSpace="nowrap"
              >
                <Icon as={MapPin} mr={2} boxSize={4} /> Geo-Controls
              </Tab>
            </TabList>

            <TabPanels>
              {/* TERMS OF SERVICE */}
              <TabPanel p={6}>
                <VStack spacing={6} align="stretch">
                  <Box>
                    <Heading size="md" color={POKER_COLORS.cyan} mb={2}>
                      Terms of Service
                    </Heading>
                    <Text color="gray.500" fontSize="sm">
                      Last Updated: {currentDate}
                    </Text>
                  </Box>

                  {!isStoreSafe() && (<Box p={4} bg="rgba(251, 191, 36, 0.08)" borderRadius="lg" borderLeft={`4px solid ${POKER_COLORS.gold}`}>
                    <Text color={POKER_COLORS.gold} fontWeight="bold" mb={2}>
                      Temporary notice about payments
                    </Text>
                    <Text color={POKER_COLORS.accentCyan} fontSize="sm">
                      We are changing card payment providers. Until the new provider is live, PlayCoins can be
                      purchased through the Apple App Store on iPhone and with cryptocurrency (USDC and others)
                      on the web. Card payments are not available on the web during this period, and no
                      purchase is ever necessary to play or to win. Redemptions are fulfilled as USDC to a
                      wallet you control or by PayPal to an account in your own name. This notice will be
                      removed when card payments return.
                    </Text>
                  </Box>)}

                  <Accordion allowMultiple>
                    <AccordionItem border="none" mb={2}>
                      <AccordionButton
                        bg={POKER_COLORS.darkBase}
                        borderRadius="md"
                        _hover={{ bg: POKER_COLORS.darkBase }}
                      >
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          1. Acceptance of Terms
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <Text mb={3}>
                          By accessing or using {settings.businessName} ("the Platform"), you agree to be bound 
                          by these Terms of Service. If you do not agree to these terms, please do not use our services.
                        </Text>
                        <Text>
                          We reserve the right to modify these terms at any time. Continued use of the Platform 
                          after changes constitutes acceptance of the modified terms.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none" mb={2}>
                      <AccordionButton 
                        bg={POKER_COLORS.darkBase} 
                        borderRadius="md"
                        _hover={{ bg: POKER_COLORS.darkBase }}
                      >
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          2. Eligibility
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <Text mb={3}>To use {settings.businessName}, you must:</Text>
                        <List spacing={2} pl={4}>
                          <ListItem>Be at least {settings.minimumAge} years of age</ListItem>
                          <ListItem>Be a legal resident of an eligible jurisdiction</ListItem>
                          <ListItem>Not be located in a restricted state: {restrictedStateNames}</ListItem>
                          <ListItem>Have the legal capacity to enter into this agreement</ListItem>
                          <ListItem>Not be prohibited by any applicable law from participating</ListItem>
                        </List>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none" mb={2}>
                      <AccordionButton 
                        bg={POKER_COLORS.darkBase} 
                        borderRadius="md"
                        _hover={{ bg: POKER_COLORS.darkBase }}
                      >
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          3. Platform Currencies
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <Text mb={3} fontWeight="bold" color={POKER_COLORS.gold}>PlayCoins:</Text>
                        <List spacing={2} pl={4} mb={4}>
                          <ListItem>Virtual currency for entertainment purposes only</ListItem>
                          <ListItem>Can be purchased or earned through daily bonuses</ListItem>
                          <ListItem>Cannot be redeemed for cash or prizes</ListItem>
                          <ListItem>Have no monetary value outside the Platform</ListItem>
                        </List>
                        <Text mb={3} fontWeight="bold" color={POKER_COLORS.gold}>PSS Coins:</Text>
                        <List spacing={2} pl={4}>
                          <ListItem>Promotional currency provided as a bonus with PlayCoin purchases</ListItem>
                          <ListItem>Also available through our free Alternative Method of Entry (AMOE)</ListItem>
                          <ListItem>Can be redeemed for prizes at a 1:1 USD value</ListItem>
                          <ListItem>Subject to playthrough requirements before redemption</ListItem>
                          <ListItem>Redemptions require identity verification</ListItem>
                        </List>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none" mb={2}>
                      <AccordionButton 
                        bg={POKER_COLORS.darkBase} 
                        borderRadius="md"
                        _hover={{ bg: POKER_COLORS.darkBase }}
                      >
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          4. Prohibited Activities
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <Text mb={3}>Users are prohibited from:</Text>
                        <List spacing={2} pl={4}>
                          <ListItem>Creating multiple accounts</ListItem>
                          <ListItem>Using VPNs, proxies, or other tools to mask location</ListItem>
                          <ListItem>Colluding with other players</ListItem>
                          <ListItem>Using bots or automated software</ListItem>
                          <ListItem>Exploiting bugs or vulnerabilities</ListItem>
                          <ListItem>Chip dumping or other forms of fraud</ListItem>
                          <ListItem>Harassing other users</ListItem>
                          <ListItem>Violating any applicable laws</ListItem>
                        </List>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none" mb={2}>
                      <AccordionButton 
                        bg={POKER_COLORS.darkBase} 
                        borderRadius="md"
                        _hover={{ bg: POKER_COLORS.darkBase }}
                      >
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          4a. Table Recordings
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <Text mb={3}>
                          Players may record a table session. While a recording runs, every player at the table sees a
                          recording badge, and seated players are asked whether they agree to appear. A player who does not
                          agree can keep playing with camera and microphone off; their username, chip count and actions may
                          still appear in the recording. The player who records may choose to show their own hole cards; other players' cards are never shown before showdown.
                        </Text>
                        <List spacing={2} pl={4}>
                          <ListItem>The player who starts a recording owns the file and is responsible for how it is shared.</ListItem>
                          <ListItem>Recordings may not be used to harass, dox or defame any player, and may not be edited to misrepresent play.</ListItem>
                          <ListItem>Recording a session may cost PSS Coins, shown before you start. Voice and video without recording are free. Partner accounts, and tables opened with a partner code, record free.</ListItem>
                          <ListItem>Files are stored for a limited period and then deleted. We may remove any recording that violates these Terms.</ListItem>
                        </List>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none" mb={2}>
                      <AccordionButton 
                        bg={POKER_COLORS.darkBase} 
                        borderRadius="md"
                        _hover={{ bg: POKER_COLORS.darkBase }}
                      >
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          5. Account Termination
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <Text>
                          We reserve the right to suspend or terminate accounts that violate these terms, 
                          engage in fraudulent activity, or for any other reason at our sole discretion. 
                          Upon termination, any unredeemed PSS Coins may be forfeited.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none" mb={2}>
                      <AccordionButton 
                        bg={POKER_COLORS.darkBase} 
                        borderRadius="md"
                        _hover={{ bg: POKER_COLORS.darkBase }}
                      >
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          6. Purchases, Refunds and Payouts
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <Text mb={2}>
                          PlayCoins are a virtual currency with no cash value, delivered to your account
                          immediately on purchase. Purchases are final once delivered, except as set out in
                          our Refund Policy. PSS Coins redemptions are fulfilled, after prize eligibility verification,
                          {isStoreSafe() ? 'by PayPal to an account in your own name.' : 'as USDC to a wallet you control or by PayPal to an account in your own name.'}
                        </Text>
                        <Text mb={2}>
                          {isStoreSafe()
                            ? 'On iPhone, PlayCoins are purchased through Apple in-app purchase. Apple handles billing and refunds under its own terms.'
                            : 'Accepted purchase methods at this time: Apple App Store (iPhone) and cryptocurrency through NOWPayments (web). Card payments will be offered again when our new card payment provider is live. Cryptocurrency payments are final once the network confirms them; an amount sent above the invoice is credited in full at the same rate, and an amount sent short is resolved by support.'}
                        </Text>
                        <Link href="/legal?tab=payouts" color={POKER_COLORS.cyan}>Read the full Payout Method and Refund Policy</Link>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none" mb={2}>
                      <AccordionButton 
                        bg={POKER_COLORS.darkBase} 
                        borderRadius="md"
                        _hover={{ bg: POKER_COLORS.darkBase }}
                      >
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          7. Contact Information
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <VStack align="start" spacing={2}>
                          <HStack>
                            <Icon as={Mail} boxSize={4} />
                            <Text>Support: {settings.supportEmail}</Text>
                          </HStack>
                          <HStack>
                            <Icon as={Globe} boxSize={4} />
                            <Text>Website: {settings.businessUrl}</Text>
                          </HStack>
                        </VStack>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </VStack>
              </TabPanel>

              {/* PRIVACY POLICY */}
              <TabPanel p={6}>
                <VStack spacing={6} align="stretch">
                  <Box>
                    <Heading size="md" color={POKER_COLORS.cyan} mb={2}>
                      Privacy Policy
                    </Heading>
                    <Text color="gray.500" fontSize="sm">
                      Last Updated: {currentDate}
                    </Text>
                  </Box>

                  <Accordion allowMultiple>
                    <AccordionItem border="none" mb={2}>
                      <AccordionButton bg={POKER_COLORS.darkBase} borderRadius="md">
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          Information We Collect
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <Text mb={3}>We collect the following information:</Text>
                        <List spacing={2} pl={4}>
                          <ListItem>Account information (email, username, date of birth)</ListItem>
                          <ListItem>Identity verification documents (for KYC)</ListItem>
                          <ListItem>Payment information (processed by our payment providers: {isStoreSafe() ? 'Apple for App Store purchases and PayPal for prize redemptions' : 'Apple for App Store purchases, NOWPayments for cryptocurrency, and PayPal for redemptions'}; we never store card or wallet keys)</ListItem>
                          <ListItem>Identity verification is performed by Didit, which processes your ID document and selfie under its own privacy policy</ListItem>
                          <ListItem>Device and browser information</ListItem>
                          <ListItem>IP address and geolocation data</ListItem>
                          <ListItem>Game history and transaction records</ListItem>
                          <ListItem>Table recordings: when a player records a table you are seated at, your video, voice, username and chat may be captured after you are shown a notice. Recordings are stored on our servers for a limited period and can be downloaded by the player who made them.</ListItem>
                        </List>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none" mb={2}>
                      <AccordionButton bg={POKER_COLORS.darkBase} borderRadius="md">
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          How We Use Your Information
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <List spacing={2} pl={4}>
                          <ListItem>To provide and maintain our services</ListItem>
                          <ListItem>To verify your identity and eligibility</ListItem>
                          <ListItem>To process transactions and redemptions</ListItem>
                          <ListItem>To detect and prevent fraud</ListItem>
                          <ListItem>To comply with legal obligations</ListItem>
                          <ListItem>To communicate with you about your account</ListItem>
                        </List>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none" mb={2}>
                      <AccordionButton bg={POKER_COLORS.darkBase} borderRadius="md">
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          Data Security
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <Text>
                          We implement industry-standard security measures to protect your data, 
                          including encryption, secure servers, and regular security audits.
                          Payments are handled by our payment providers on their own PCI-DSS compliant
                          systems; card and wallet details are never entered on, or stored by, {settings.businessName}.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none" mb={2}>
                      <AccordionButton bg={POKER_COLORS.darkBase} borderRadius="md">
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          Your Rights
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <Text mb={3}>You have the right to:</Text>
                        <List spacing={2} pl={4}>
                          <ListItem>Access your personal data</ListItem>
                          <ListItem>Request correction of inaccurate data</ListItem>
                          <ListItem>Request deletion of your data (subject to legal requirements)</ListItem>
                          <ListItem>Opt out of marketing communications</ListItem>
                        </List>
                        <Text mt={3}>
                          To exercise these rights, contact us at {settings.privacyEmail}
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </VStack>
              </TabPanel>

              {/* SWEEPSTAKES RULES */}
              <TabPanel p={6}>
                <VStack spacing={6} align="stretch">
                  <Box>
                    <Heading size="md" color={POKER_COLORS.cyan} mb={2}>
                      Official Sweepstakes Rules
                    </Heading>
                    <Text color="gray.500" fontSize="sm">
                      Last Updated: {currentDate}
                    </Text>
                  </Box>

                  <Box p={4} bg={POKER_COLORS.darkBase} borderRadius="lg" borderLeft={`4px solid ${POKER_COLORS.gold}`}>
                    <Text color={POKER_COLORS.gold} fontWeight="bold" mb={2}>
                      NO PURCHASE NECESSARY TO ENTER OR WIN
                    </Text>
                    <Text color={POKER_COLORS.accentCyan} fontSize="sm">
                      A purchase will not increase your chances of winning. 
                      See "Alternative Method of Entry" tab for free entry instructions.
                    </Text>
                  </Box>

                  <Accordion allowMultiple>
                    <AccordionItem border="none" mb={2}>
                      <AccordionButton bg={POKER_COLORS.darkBase} borderRadius="md">
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          Eligibility
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <List spacing={2} pl={4}>
                          <ListItem>Open to legal residents of the United States</ListItem>
                          <ListItem>Must be {settings.minimumAge} years of age or older</ListItem>
                          <ListItem>Void in: {restrictedStateNames}</ListItem>
                          <ListItem>Employees of {settings.businessName} and their families are not eligible</ListItem>
                        </List>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none" mb={2}>
                      <AccordionButton bg={POKER_COLORS.darkBase} borderRadius="md">
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          How to Enter
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <Text mb={3} fontWeight="bold">Method 1: Purchase</Text>
                        <Text mb={4}>
                          Purchase PlayCoins and receive PSS Coins as a free bonus. 
                          Each $1 spent grants 1,000 PlayCoins + $1 PSS Coins.
                        </Text>
                        <Text mb={3} fontWeight="bold">Method 2: Free Entry (AMOE)</Text>
                        <Text>
                          Receive ${settings.amoeAmount.toFixed(2)} PSS Coins for free once every {settings.amoeCooldownHours} hours 
                          when your PSS Coins balance is $0. No purchase required. 
                          See "Free Entry" tab for complete instructions.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none" mb={2}>
                      <AccordionButton bg={POKER_COLORS.darkBase} borderRadius="md">
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          PSS Coins Redemption
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <List spacing={2} pl={4}>
                          <ListItem>PSS Coins can be redeemed for prizes at a rate of $1 PSS Coins = $1 USD</ListItem>
                          <ListItem>Minimum redemption: ${settings.minRedemption.toFixed(2)}</ListItem>
                          <ListItem>Maximum daily redemption: ${settings.maxRedemptionDaily.toFixed(2)}</ListItem>
                          <ListItem>Maximum weekly redemption: ${settings.maxRedemptionWeekly.toFixed(2)}</ListItem>
                          <ListItem>Playthrough requirement: {settings.playthroughRequirement}x the bonus amount</ListItem>
                          <ListItem>Identity verification required for all redemptions</ListItem>
                          <ListItem>Processing time: {settings.redemptionProcessingDays}</ListItem>
                        </List>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none" mb={2}>
                      <AccordionButton bg={POKER_COLORS.darkBase} borderRadius="md">
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          Game Rules
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <Text mb={3}>
                          All poker games follow standard Texas Hold'em rules. Players compete against 
                          each other, not against the house. A small rake (up to 5%, capped at $5) 
                          is collected from each pot.
                        </Text>
                        <Text mb={3}>
                          Results are determined by player skill and the random distribution of cards
                          using a cryptographically secure random number generator.
                        </Text>
                        <Text>
                          We use no algorithms that induce more hands played or bigger pots.
                          Every deal is completely random. See the "Fair Play" tab for our full
                          Fair Play and Random Dealing Guarantee.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none" mb={2}>
                      <AccordionButton bg={POKER_COLORS.darkBase} borderRadius="md">
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          Skill Disclosure
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <Text mb={3}>
                          Poker on {settings.businessName} is a peer-to-peer game in which skill is the
                          predominant factor over time. Players compete only against other players. The
                          house never takes a seat, never holds a stake in any hand, and earns only the
                          disclosed rake.
                        </Text>
                        <Text mb={3}>
                          The cards are dealt by a certified random number generator, so the element of
                          chance in any single hand is identical for every player at the table. Results
                          across many hands are driven by each player's decisions: which hands to play,
                          how much to bet, when to fold. No outcome is influenced by the house, by
                          purchase history, or by account balance.
                        </Text>
                        <Text>
                          Free practice tables, training scenarios and a coach are available to every
                          player at no cost so that skill can be developed without any purchase.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none" mb={2}>
                      <AccordionButton bg={POKER_COLORS.darkBase} borderRadius="md">
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          Sponsor & Disclaimer
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <Text mb={3}>
                          This sweepstakes is sponsored solely by {settings.businessName}.
                          For questions or concerns, contact {settings.supportEmail}.
                        </Text>
                        <Text mb={3} fontWeight="bold" color={POKER_COLORS.gold}>
                          Apple Disclaimer
                        </Text>
                        <Text>
                          Apple Inc. is not a sponsor of, and is not involved in any way with, this
                          contest or sweepstakes. This promotion is in no way sponsored, endorsed,
                          administered by, or associated with Apple Inc.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </VStack>
              </TabPanel>

              {/* FAIR PLAY & RNG GUARANTEE */}
              <TabPanel p={6}>
                <VStack spacing={6} align="stretch">
                  <Box>
                    <Heading size="md" color={POKER_COLORS.cyan} mb={2}>
                      Fair Play and Random Dealing Guarantee
                    </Heading>
                    <Text color="gray.500" fontSize="sm">
                      Last Updated: {currentDate}
                    </Text>
                  </Box>

                  <Box p={4} bg={POKER_COLORS.darkBase} borderRadius="lg" borderLeft={`4px solid ${POKER_COLORS.gold}`}>
                    <Text color={POKER_COLORS.gold} fontWeight="bold" mb={2}>
                      EVERY DEAL IS 100% RANDOM
                    </Text>
                    <Text color={POKER_COLORS.accentCyan} fontSize="sm">
                      Every card on {settings.businessName} is dealt by a cryptographically
                      secure random number generator. We have absolutely no algorithm that
                      induces more hands played, builds bigger pots, or influences which
                      cards any player receives. Ever.
                    </Text>
                  </Box>

                  <Accordion allowMultiple defaultIndex={[0]}>
                    <AccordionItem border="none" mb={2}>
                      <AccordionButton bg={POKER_COLORS.darkBase} borderRadius="md">
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          How Every Hand Is Dealt
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <List spacing={2} pl={4}>
                          <ListItem>
                            A complete 52-card deck is freshly shuffled before every single hand.
                          </ListItem>
                          <ListItem>
                            The shuffle is a Fisher-Yates shuffle driven by a cryptographically
                            secure random number generator (CSRNG), the same class of randomness
                            used in banking and encryption. It is not seeded by, and cannot be
                            influenced by, anything happening in the game.
                          </ListItem>
                          <ListItem>
                            The full deck order, including all community cards, is fixed the moment
                            the hand begins, before any betting takes place. Nothing that happens
                            during the hand can change which cards will come.
                          </ListItem>
                          <ListItem>
                            Shuffling and dealing happen on our secure servers. Players, employees,
                            and administrators have no ability to see or alter the deck.
                          </ListItem>
                        </List>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none" mb={2}>
                      <AccordionButton bg={POKER_COLORS.darkBase} borderRadius="md">
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          What We Never Do
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <Text mb={3}>
                          {settings.businessName} does not use, and will never use, any of the
                          following:
                        </Text>
                        <List spacing={2} pl={4}>
                          <ListItem>
                            No "action" algorithms that deal more playable hands to keep players
                            at the table or increase the number of hands played.
                          </ListItem>
                          <ListItem>
                            No pot-building algorithms that set up big hands against each other
                            (for example, dealing one player a flush and another a full house) to
                            inflate pot sizes or rake.
                          </ListItem>
                          <ListItem>
                            No card weighting of any kind: your chance of being dealt any hand is
                            exactly the mathematical probability of a fair 52-card deck.
                          </ListItem>
                          <ListItem>
                            No dealing that varies by stakes, balance, win/loss history, purchase
                            history, session length, or any other player attribute.
                          </ListItem>
                          <ListItem>
                            No house players in cash games or tournaments: you compete against
                            other players, never against the house.
                          </ListItem>
                        </List>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none" mb={2}>
                      <AccordionButton bg={POKER_COLORS.darkBase} borderRadius="md">
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          The House Has No Stake in Who Wins
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <Text>
                          Our revenue comes solely from a fixed, capped rake that is the same no
                          matter who wins the pot. We earn nothing extra when pots are bigger for
                          one player over another, and nothing from any player losing. There is no
                          financial incentive, and no technical mechanism, for the deal to favor
                          or disfavor anyone.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none" mb={2}>
                      <AccordionButton bg={POKER_COLORS.darkBase} borderRadius="md">
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          Accountability
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <List spacing={2} pl={4}>
                          <ListItem>
                            Every hand is recorded. Your recent hand history is available at the
                            table, so you can review exactly what was dealt and how it played out.
                          </ListItem>
                          <ListItem>
                            We actively monitor for collusion, bots, and unfair play to protect
                            the integrity of every game.
                          </ListItem>
                          <ListItem>
                            If you ever have a concern about a specific hand, contact{' '}
                            {settings.supportEmail} with the table and hand number and we will
                            review it.
                          </ListItem>
                        </List>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </VStack>
              </TabPanel>

              {/* AMOE INSTRUCTIONS */}
              <TabPanel p={6}>
                <VStack spacing={6} align="stretch">
                  <Box>
                    <Heading size="md" color={POKER_COLORS.cyan} mb={2}>
                      Free Alternative Method of Entry (AMOE)
                    </Heading>
                    <Text color="gray.500" fontSize="sm">
                      No Purchase Necessary
                    </Text>
                  </Box>

                  <Box 
                    p={6} 
                    bg="linear-gradient(135deg, rgba(212, 175, 55, 0.2) 0%, rgba(0, 217, 217, 0.2) 100%)"
                    borderRadius="lg"
                    border={`2px solid ${POKER_COLORS.gold}`}
                  >
                    <VStack spacing={4}>
                      <Icon as={Gift} boxSize={12} color={POKER_COLORS.gold} />
                      <Heading size="lg" color={POKER_COLORS.gold} textAlign="center">
                        Get ${settings.amoeAmount.toFixed(2)} PSS Coins FREE
                      </Heading>
                      <Text color={POKER_COLORS.accentCyan} textAlign="center">
                        No purchase required. Available once every {settings.amoeCooldownHours} hours.
                      </Text>
                    </VStack>
                  </Box>

                  <Box p={4} bg={POKER_COLORS.darkBase} borderRadius="lg">
                    <Heading size="sm" color={POKER_COLORS.cyan} mb={4}>
                      How to Claim Your Free PSS Coins:
                    </Heading>
                    <VStack align="stretch" spacing={4}>
                      <HStack align="start">
                        <Box 
                          w={8} h={8} 
                          borderRadius="full" 
                          bg={POKER_COLORS.gold} 
                          color={POKER_COLORS.darkTeal}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          fontWeight="bold"
                          flexShrink={0}
                        >
                          1
                        </Box>
                        <Box>
                          <Text color={POKER_COLORS.cyan} fontWeight="bold">Create an Account</Text>
                          <Text color={POKER_COLORS.accentCyan} fontSize="sm">
                            Sign up for free at {settings.businessUrl}
                          </Text>
                        </Box>
                      </HStack>
                      <HStack align="start">
                        <Box 
                          w={8} h={8} 
                          borderRadius="full" 
                          bg={POKER_COLORS.gold} 
                          color={POKER_COLORS.darkTeal}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          fontWeight="bold"
                          flexShrink={0}
                        >
                          2
                        </Box>
                        <Box>
                          <Text color={POKER_COLORS.cyan} fontWeight="bold">Verify Your Age</Text>
                          <Text color={POKER_COLORS.accentCyan} fontSize="sm">
                            Confirm you are {settings.minimumAge} years or older by providing your date of birth
                          </Text>
                        </Box>
                      </HStack>
                      <HStack align="start">
                        <Box 
                          w={8} h={8} 
                          borderRadius="full" 
                          bg={POKER_COLORS.gold} 
                          color={POKER_COLORS.darkTeal}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          fontWeight="bold"
                          flexShrink={0}
                        >
                          3
                        </Box>
                        <Box>
                          <Text color={POKER_COLORS.cyan} fontWeight="bold">Go to Your Wallet</Text>
                          <Text color={POKER_COLORS.accentCyan} fontSize="sm">
                            Navigate to the Wallet page in your account
                          </Text>
                        </Box>
                      </HStack>
                      <HStack align="start">
                        <Box 
                          w={8} h={8} 
                          borderRadius="full" 
                          bg={POKER_COLORS.gold} 
                          color={POKER_COLORS.darkTeal}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          fontWeight="bold"
                          flexShrink={0}
                        >
                          4
                        </Box>
                        <Box>
                          <Text color={POKER_COLORS.cyan} fontWeight="bold">Claim Free PSS Coins</Text>
                          <Text color={POKER_COLORS.accentCyan} fontSize="sm">
                            When your PSS Coins balance is $0, click the "Claim Free PSS Coins" button
                          </Text>
                        </Box>
                      </HStack>
                    </VStack>
                  </Box>

                  <Box p={4} bg={POKER_COLORS.darkBase} borderRadius="lg">
                    <Heading size="sm" color={POKER_COLORS.cyan} mb={3}>
                      Eligibility Requirements:
                    </Heading>
                    <List spacing={2} pl={4} color={POKER_COLORS.accentCyan}>
                      <ListItem>Must be {settings.minimumAge} years of age or older</ListItem>
                      <ListItem>Must be located in an eligible US state</ListItem>
                      <ListItem>PSS Coins balance must be $0</ListItem>
                      <ListItem>Must not have claimed within the past {settings.amoeCooldownHours} hours</ListItem>
                      <ListItem>Must not be using a VPN or proxy</ListItem>
                      <ListItem>One claim per account per {settings.amoeCooldownHours}-hour period</ListItem>
                    </List>
                  </Box>

                  <Box p={4} bg="rgba(251, 191, 36, 0.1)" borderRadius="lg" borderLeft={`4px solid #fbbf24`}>
                    <Text color="#fbbf24" fontWeight="bold" mb={2}>
                      Important Note
                    </Text>
                    <Text color={POKER_COLORS.accentCyan} fontSize="sm">
                      Free PSS Coins entries have the same redemption value and playthrough requirements 
                      as purchased entries. Identity verification is required to redeem PSS Coins for prizes.
                    </Text>
                  </Box>
                </VStack>
              </TabPanel>

              {/* RESPONSIBLE GAMING */}
              <TabPanel p={6}>
                <VStack spacing={6} align="stretch">
                  <Box>
                    <Heading size="md" color={POKER_COLORS.cyan} mb={2}>
                      Responsible Gaming
                    </Heading>
                    <Text color="gray.500" fontSize="sm">
                      Play Responsibly
                    </Text>
                  </Box>

                  <Box p={4} bg={POKER_COLORS.darkBase} borderRadius="lg" borderLeft={`4px solid ${POKER_COLORS.cyan}`}>
                    <Text color={POKER_COLORS.cyan} fontWeight="bold" mb={2}>
                      Our Commitment
                    </Text>
                    <Text color={POKER_COLORS.accentCyan}>
                      {settings.businessName} is committed to promoting responsible gaming. We provide tools 
                      and resources to help you stay in control of your playing habits.
                    </Text>
                  </Box>

                  <Accordion allowMultiple>
                    <AccordionItem border="none" mb={2}>
                      <AccordionButton bg={POKER_COLORS.darkBase} borderRadius="md">
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          Set Your Limits
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <Text mb={3}>You can set personal limits in your profile settings:</Text>
                        <List spacing={2} pl={4}>
                          <ListItem>Daily purchase limits</ListItem>
                          <ListItem>Weekly purchase limits</ListItem>
                          <ListItem>Session time limits</ListItem>
                          <ListItem>Loss limits</ListItem>
                        </List>
                        <Text mt={3}>
                          These limits can be decreased immediately but require a 24-hour cooling-off 
                          period to increase.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none" mb={2}>
                      <AccordionButton bg={POKER_COLORS.darkBase} borderRadius="md">
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          Warning Signs
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <Text mb={3}>Consider taking a break if you:</Text>
                        <List spacing={2} pl={4}>
                          <ListItem>Spend more time or money than you intended</ListItem>
                          <ListItem>Play to escape problems or relieve stress</ListItem>
                          <ListItem>Feel irritable when trying to cut down</ListItem>
                          <ListItem>Lie to others about your playing habits</ListItem>
                          <ListItem>Borrow money or sell possessions to play</ListItem>
                          <ListItem>Neglect responsibilities or relationships</ListItem>
                        </List>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none" mb={2}>
                      <AccordionButton bg={POKER_COLORS.darkBase} borderRadius="md">
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          Self-Exclusion
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <Text>
                          If you need a break, you can request self-exclusion for a period of 24 hours, 
                          7 days, 30 days, or 6 months. During this time, you will not be able to 
                          access your account or make any purchases. Contact support at{' '}
                          {settings.supportEmail} to request self-exclusion.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none" mb={2}>
                      <AccordionButton bg={POKER_COLORS.darkBase} borderRadius="md">
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          Resources
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <Text mb={3}>If you or someone you know needs help:</Text>
                        <VStack align="start" spacing={2}>
                          <Link href="https://www.ncpgambling.org/" isExternal color={POKER_COLORS.cyan}>
                            National Council on Problem Gambling: 1-800-522-4700
                          </Link>
                          <Link href="https://www.gamblersanonymous.org/" isExternal color={POKER_COLORS.cyan}>
                            Gamblers Anonymous
                          </Link>
                          <Link href="https://www.gam-anon.org/" isExternal color={POKER_COLORS.cyan}>
                            Gam-Anon (for families)
                          </Link>
                        </VStack>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>

                  <Box p={4} bg="rgba(16, 185, 129, 0.1)" borderRadius="lg" borderLeft={`4px solid #10b981`}>
                    <Text color="#10b981" fontWeight="bold" mb={2}>
                      Remember
                    </Text>
                    <Text color={POKER_COLORS.accentCyan}>
                      Playing should be fun! Set a budget, stick to it, and never play with money 
                      you can't afford to lose. If it stops being fun, it's time to take a break.
                    </Text>
                  </Box>
                </VStack>
              </TabPanel>

              {/* PAYOUT METHOD & REFUND POLICY */}
              <TabPanel p={6}>
                <VStack spacing={6} align="stretch">
                  <Box>
                    <Heading size="md" color={POKER_COLORS.cyan} mb={2}>
                      Payout Method Policy
                    </Heading>
                    <Text color="gray.500" fontSize="sm">
                      Last Updated: {currentDate}
                    </Text>
                  </Box>

                  <Box p={4} bg={POKER_COLORS.darkBase} borderRadius="lg" borderLeft={`4px solid ${POKER_COLORS.gold}`}>
                    <Text color={POKER_COLORS.gold} fontWeight="bold" mb={2}>
                      How redemptions are fulfilled
                    </Text>
                    <Text color={POKER_COLORS.accentCyan} fontSize="sm">
                      PSS Coins redemptions are fulfilled, after prize eligibility verification, {isStoreSafe() ? 'by PayPal to the email or phone number on the player\'s own account.' : 'either as USDC to a wallet the player controls, or by PayPal to the email or phone number on the player\'s own account.'} Redemptions are not fulfilled in cash, gift cards, or to any
                      account in another person's name. A newly added wallet address can receive a
                      redemption 24 hours after it is added.
                    </Text>
                    <Text color={POKER_COLORS.accentCyan} fontSize="sm" mt={2}>
                      These redemption methods are in place while we onboard a new payment provider. Bank
                      transfer will be offered again once that provider is live.
                    </Text>
                  </Box>

                  <Accordion allowMultiple>
                    <AccordionItem border="none" mb={2}>
                      <AccordionButton bg={POKER_COLORS.darkBase} borderRadius="md">
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          Eligibility to redeem
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <List spacing={2} pl={4}>
                          <ListItem>One-time identity verification before the first redemption: government photo ID, date of birth ({settings.minimumAge}+), and a residential address in an eligible state</ListItem>
                          <ListItem>Bonus PSS Coins must be played through {settings.playthroughRequirement}x before it can be redeemed</ListItem>
                          <ListItem>Account in good standing, with no open dispute, chargeback or fraud review</ListItem>
                          <ListItem>Redemption is not available to players located in: {restrictedStateNames}</ListItem>
                        </List>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none" mb={2}>
                      <AccordionButton bg={POKER_COLORS.darkBase} borderRadius="md">
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          Limits, timing and fees
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <List spacing={2} pl={4}>
                          <ListItem>PSS Coins is redeemed at $1 PSS Coins = $1 USD</ListItem>
                          <ListItem>Minimum redemption: ${settings.minRedemption.toFixed(2)}</ListItem>
                          <ListItem>Maximum: ${settings.maxRedemptionDaily.toLocaleString()} per day, ${settings.maxRedemptionWeekly.toLocaleString()} per week, ${settings.maxRedemptionMonthly.toLocaleString()} per month</ListItem>
                          <ListItem>Every request is reviewed by our team. Standard payouts are sent within {settings.redemptionProcessingDays} of approval, and larger or flagged requests may be held for additional review</ListItem>
                          <ListItem>PayPal payouts carry no fee from us; USDC payouts carry only the network fee</ListItem>
                          <ListItem>Requests are made from the Wallet in the app, and their status is shown there at every step</ListItem>
                        </List>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>

                  <Divider borderColor={`${POKER_COLORS.cyan}33`} />

                  <Box>
                    <Heading size="md" color={POKER_COLORS.cyan} mb={2}>
                      Refund Policy
                    </Heading>
                  </Box>

                  <Accordion allowMultiple>
                    <AccordionItem border="none" mb={2}>
                      <AccordionButton bg={POKER_COLORS.darkBase} borderRadius="md">
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          What you are buying
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <Text>
                          Purchases are for PlayCoins, a virtual currency used only inside {settings.businessName}.
                          PlayCoins have no cash value, cannot be redeemed, and are delivered to your account
                          immediately. Any PSS Coins included with a purchase is a free promotional bonus, not
                          something you buy. Because the product is delivered instantly, purchases are final
                          once the coins are in your account, except in the cases below.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none" mb={2}>
                      <AccordionButton bg={POKER_COLORS.darkBase} borderRadius="md">
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          When we refund
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <List spacing={2} pl={4} mb={3}>
                          <ListItem>You were charged more than once for the same purchase</ListItem>
                          <ListItem>The coins were not delivered to your account</ListItem>
                          <ListItem>The purchase was not authorized by you (we will also secure the account)</ListItem>
                        </List>
                        <Text mb={2}>
                          Contact {settings.supportEmail} or in-app Support within 30 days of the charge with the
                          date and amount. Approved refunds go back to the original payment method, usually
                          within 5 to 10 business days depending on your bank. Any bonus PSS Coins that came with
                          a refunded purchase, and any PSS Coins won with it, is removed from the account.
                        </Text>
                        <Text mb={2}>
                          Purchases made through the Apple App Store are refunded by Apple under Apple's own
                          refund process; we will help with the request.
                        </Text>
                        <Text>
                          Cryptocurrency purchases are final once the network confirms them, because the
                          payment cannot be reversed. If you were charged twice or the coins were not
                          delivered, we correct it as PlayCoins or as USDC returned to the sending wallet.
                          A payment that arrives over the invoiced amount is credited in full at the same
                          rate; a payment that arrives short is held and resolved by support. Card payments
                          are temporarily unavailable while we onboard a new payment provider; cryptocurrency
                          is offered as a temporary option in the meantime.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none" mb={2}>
                      <AccordionButton bg={POKER_COLORS.darkBase} borderRadius="md">
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          Disputes and chargebacks
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <Text>
                          Please contact us before disputing a charge with your bank; nearly every issue is
                          resolved the same day. A chargeback on a delivered purchase places the account under
                          review, and purchases and redemptions are paused until it is resolved.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </VStack>
              </TabPanel>

              {/* GEO-CONTROLS */}
              <TabPanel p={6}>
                <VStack spacing={6} align="stretch">
                  <Box>
                    <Heading size="md" color={POKER_COLORS.cyan} mb={2}>
                      Geo-Controls Procedures
                    </Heading>
                    <Text color="gray.500" fontSize="sm">
                      Last Updated: {currentDate}
                    </Text>
                  </Box>

                  <Box p={4} bg={POKER_COLORS.darkBase} borderRadius="lg" borderLeft={`4px solid ${POKER_COLORS.cyan}`}>
                    <Text color={POKER_COLORS.cyan} fontWeight="bold" mb={2}>
                      Where the sweepstakes is offered
                    </Text>
                    <Text color={POKER_COLORS.accentCyan} fontSize="sm">
                      {settings.businessName} is offered to residents of the United States only. Free play
                      with PlayCoins is available in every state. PSS Coins purchase bonuses and prize
                      redemption are not available in: {restrictedStateNames}.
                    </Text>
                  </Box>

                  <Accordion allowMultiple>
                    <AccordionItem border="none" mb={2}>
                      <AccordionButton bg={POKER_COLORS.darkBase} borderRadius="md">
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          How location is verified
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <List spacing={2} pl={4}>
                          <ListItem>Every sign-in runs an IP geolocation check that resolves the player's country and state, and the check is repeated during play</ListItem>
                          <ListItem>Connections that resolve outside the United States are blocked</ListItem>
                          <ListItem>Connections from a restricted state are placed in free-play-only mode: PSS Coins purchases and redemptions are disabled and the player is told why</ListItem>
                          <ListItem>Traffic that presents as a VPN, proxy, or hosting provider is treated as unverifiable and blocked{settings.vpnBlockingEnabled ? '' : ' where detection is available'}</ListItem>
                          <ListItem>The check fails closed: if location cannot be verified, access is denied rather than allowed</ListItem>
                          <ListItem>Every check and its outcome is logged with a timestamp for compliance review</ListItem>
                        </List>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none" mb={2}>
                      <AccordionButton bg={POKER_COLORS.darkBase} borderRadius="md">
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          Identity and address at redemption
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <List spacing={2} pl={4}>
                          <ListItem>Before any redemption is fulfilled, the player completes identity verification with a government photo ID and proof of age ({settings.minimumAge}+)</ListItem>
                          <ListItem>The verified residential address must be in an eligible state; a restricted-state address blocks redemption regardless of where the player is connecting from</ListItem>
                          <ListItem>Redemptions go only to a wallet or PayPal account in the verified name</ListItem>
                          <ListItem>Accounts whose location history and verified address disagree are held for manual review</ListItem>
                        </List>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none" mb={2}>
                      <AccordionButton bg={POKER_COLORS.darkBase} borderRadius="md">
                        <Box flex="1" textAlign="left" color={POKER_COLORS.cyan} fontWeight="bold">
                          Account controls
                        </Box>
                        <AccordionIcon color={POKER_COLORS.cyan} />
                      </AccordionButton>
                      <AccordionPanel pb={4} color={POKER_COLORS.accentCyan}>
                        <List spacing={2} pl={4}>
                          <ListItem>Limits on accounts per device and per IP address, with device fingerprinting at sign-up</ListItem>
                          <ListItem>Suspicious patterns (rapid location changes, shared devices, repeated blocked attempts) flag the account for review and can freeze redemptions</ListItem>
                          <ListItem>Restricted-state lists are maintained centrally and updated as state law changes</ListItem>
                          <ListItem>Questions about a location decision: {settings.supportEmail}</ListItem>
                        </List>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </PokerCard>

        {/* Footer */}
        <Box textAlign="center" py={4}>
          <Text color="gray.500" fontSize="sm">
            © {new Date().getFullYear()} {settings.businessName}. All rights reserved.
          </Text>
          <HStack justify="center" spacing={4} mt={2}>
            <Link href={`mailto:${settings.supportEmail}`} color={POKER_COLORS.cyan} fontSize="sm">
              Support
            </Link>
            <Link href={`mailto:${settings.privacyEmail}`} color={POKER_COLORS.cyan} fontSize="sm">
              Privacy Inquiries
            </Link>
          </HStack>
        </Box>
      </VStack>
    </Container>
  );
};

export default LegalPage;